import { Controller } from "@hotwired/stimulus"

const TRANSITION_FALLBACK_MS = 500
const AUTO_DOWNLOAD_DEDUPE_MS = 15_000
/** Prefix for per-token keys; pruned when older than {@link AUTO_DOWNLOAD_DEDUPE_MS}. */
const AUTO_DOWNLOAD_KEY_PREFIX = "hudu-auto-print-dl:"
/** Avoid scanning localStorage on every auto-download check. */
const AUTO_DOWNLOAD_PRUNE_INTERVAL_MS = 60_000

let lastAutoDownloadPruneAt = 0

/** Resets throttled prune clock — use from tests so pruning scenarios stay isolated. */
export function resetAutoDownloadPruneClockForTests() {
  lastAutoDownloadPruneAt = 0
}

/**
 * Removes all `hudu-auto-print-dl:*` entries whose timestamp is outside the dedupe window
 * (or invalid), so keys do not accumulate across many exports.
 */
function pruneExpiredAutoDownloadEntries() {
  const now = Date.now()
  try {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(AUTO_DOWNLOAD_KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) {
      const prev = localStorage.getItem(key)
      if (!prev) {
        localStorage.removeItem(key)
        continue
      }
      const age = now - Number(prev)
      if (Number.isNaN(age) || age >= AUTO_DOWNLOAD_DEDUPE_MS) {
        localStorage.removeItem(key)
      }
    }
  } catch {
    // ignore
  }
}

function maybePruneExpiredAutoDownloadEntries() {
  const now = Date.now()
  if (now - lastAutoDownloadPruneAt < AUTO_DOWNLOAD_PRUNE_INTERVAL_MS) return
  lastAutoDownloadPruneAt = now
  pruneExpiredAutoDownloadEntries()
}

/**
 * Stable localStorage suffix for deduping one automatic download per logical export URL.
 * Signed exports use `?token=`; session-based file URLs use `/exports_file/:id`.
 * @param {string} href
 */
function autoDownloadDedupeStorageSuffix(href) {
  if (!href || !href.trim()) return null
  try {
    const u = new URL(href, window.location.origin)
    const token = u.searchParams.get("token")
    if (token) return token

    const fileMatch = u.pathname.match(/^\/exports_file\/(\d+)\/?$/)
    if (fileMatch) return `file:${fileMatch[1]}`

    const normalized = `${u.pathname}${u.search}`
    if (normalized && normalized !== "/") return `url:${normalized}`

    return null
  } catch {
    return null
  }
}

/** @param {string} href */
function autoDownloadDedupeKey(href) {
  const suffix = autoDownloadDedupeStorageSuffix(href)
  return suffix ? `${AUTO_DOWNLOAD_KEY_PREFIX}${suffix}` : null
}

/** Exposes {@link autoDownloadDedupeKey} for unit tests. */
export function autoDownloadDedupeKeyForTests(href) {
  return autoDownloadDedupeKey(href)
}

/** @param {string} href */
function shouldAllowAutoDownloadFromStorage(href) {
  const key = autoDownloadDedupeKey(href)
  if (!key) return true
  maybePruneExpiredAutoDownloadEntries()
  try {
    const now = Date.now()
    const prev = localStorage.getItem(key)
    if (!prev) return true
    const age = now - Number(prev)
    if (Number.isNaN(age)) {
      localStorage.removeItem(key)
      return true
    }
    if (age < AUTO_DOWNLOAD_DEDUPE_MS) return false
    localStorage.removeItem(key)
    return true
  } catch {
    return true
  }
}

/** @param {string} href */
function recordAutoDownloadSlot(href) {
  const key = autoDownloadDedupeKey(href)
  if (!key) return
  maybePruneExpiredAutoDownloadEntries()
  try {
    localStorage.setItem(key, String(Date.now()))
  } catch (e) {
    const isQuota =
      e &&
      (e.name === "QuotaExceededError" ||
        e.code === 22 ||
        (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "QuotaExceededError"))
    if (isQuota) {
      pruneExpiredAutoDownloadEntries()
      try {
        localStorage.setItem(key, String(Date.now()))
      } catch {
        // ignore
      }
    }
  }
}

/** @param {HTMLElement} element */
function delegationStorageKey(element) {
  return `hudu-bg-progress-delegated:${element.id}`
}

/**
 * @param {HTMLElement} element
 * @param {{ downloadTriggered?: string, backgroundProgressDownloadDelegated?: string }} dataset
 */
function shouldAttemptAutoDownload(element, dataset) {
  if (dataset.downloadTriggered) return false
  if (dataset.backgroundProgressDownloadDelegated === "true") return false
  try {
    if (sessionStorage.getItem(delegationStorageKey(element)) === "1") return false
  } catch {
    // sessionStorage may be unavailable (private mode, security errors)
  }
  return true
}

/** @param {HTMLElement} element */
function recordDownloadDelegatedToOtherTab(element) {
  element.dataset.backgroundProgressDownloadDelegated = "true"
  try {
    sessionStorage.setItem(delegationStorageKey(element), "1")
  } catch {
    // sessionStorage may be unavailable; dataset still suppresses same-node reconnects
  }
}

const REFRESHABLE_STATUSES = new Set(["prepping", "in_progress"])

export default class extends Controller {
  static targets = ["downloadLink", "message", "progressBar", "bar"]
  static values = {
    id: Number,
    status: String,
    persist: { type: Boolean, default: false },
    dismissDelay: { type: Number, default: 5000 },
    refreshInterval: { type: Number, default: 45_000 },
  }

  connect() {
    if (this.statusValue === "complete") {
      // Persistent completes carry a visible download link, so they stay open
      // until dismissed (like errors). The auto-download is still attempted;
      // the visible link is the fallback when the browser blocks it.
      if (!this.persistValue) this.#scheduleAutoDismiss()
      if (shouldAttemptAutoDownload(this.element, this.element.dataset)) {
        this.#triggerDownload()
      }
    } else if (REFRESHABLE_STATUSES.has(this.statusValue)) {
      this.#scheduleRefresh()
    }
  }

  disconnect() {
    clearTimeout(this.#autoDismissTimer)
    clearTimeout(this.#refreshTimer)
    // Turbo re-attaches this permanent-frame element on navigation, which
    // restarts the dismiss timer. Remove completed toasts so rapid nav
    // can't pin them. Errors and persistent completes (those with a download
    // link) stay put; they shouldn't vanish before the user acts on them.
    // Delegation marker is left alone on purpose: it's session-scoped
    // dedupe for future remounts of the same progress row.
    if (this.statusValue === "complete" && !this.persistValue) {
      this.element.remove()
    }
  }

  close() {
    // The programmatic auto-download fires .click() on the same anchor that
    // carries this action; ignore it so a browser-blocked download can't
    // silently dismiss the toast. Only genuine user clicks (or the auto-dismiss
    // timer, which passes no event) should tear it down.
    if (this.#autoClicking) return

    clearTimeout(this.#autoDismissTimer)
    this.#clearDelegationMarker()
    const remove = () => this.element.remove()
    this.element.addEventListener("transitionend", remove, { once: true })
    setTimeout(remove, TRANSITION_FALLBACK_MS)
    this.element.classList.add("background-progress--dismissing")
  }

  // --- private ---

  #autoDismissTimer = null
  #refreshTimer = null
  #autoClicking = false

  #clearDelegationMarker() {
    this.element.removeAttribute("data-background-progress-download-delegated")
    try {
      sessionStorage.removeItem(delegationStorageKey(this.element))
    } catch {
      // ignore
    }
  }

  #triggerDownload() {
    if (!this.hasDownloadLinkTarget) return
    const href = this.downloadLinkTarget.getAttribute("href")
    if (!href || !this.#isSafeUrl(href)) return

    const element = this.element
    const downloadLink = this.downloadLinkTarget

    const markAndClick = () => {
      if (element.dataset.downloadTriggered) return
      // Same signed URL is broadcast to every open tab (each has its own ActionCable subscriber).
      // Web Locks usually let only one tab click, but locks may be missing or unreliable; localStorage
      // is origin-wide so duplicate stream deliveries or extra tabs only trigger one auto-download
      // per token within a short window.
      // Only *read* before click — if we wrote the timestamp before clicking, a blocked/failed
      // download would suppress retries until the window expires.
      if (!shouldAllowAutoDownloadFromStorage(href)) return
      element.dataset.downloadTriggered = "true"
      this.#autoClicking = true
      try {
        downloadLink.click()
      } finally {
        this.#autoClicking = false
      }
      recordAutoDownloadSlot(href)
    }

    // Export/print progress is broadcast to every open tab (turbo_stream_from current_user).
    // Web Locks are shared per origin across tabs so only one tab initiates the download.
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      const lockName = `hudu-background-progress:${element.id}`
      // When lock == null, persist delegation (dataset + sessionStorage) so this tab does not
      // retry after Turbo replaces the node or Stimulus reconnects while downloadTriggered is still unset.
      void Promise.resolve(
        navigator.locks.request(lockName, { ifAvailable: true }, (lock) => {
          if (lock == null) {
            recordDownloadDelegatedToOtherTab(element)
            return
          }
          markAndClick()
        }),
      ).catch(() => {
        if (!shouldAttemptAutoDownload(element, element.dataset)) return
        // Locks API failed entirely (e.g. SecurityError); same best-effort path as when locks are missing.
        markAndClick()
      })
    } else {
      markAndClick()
    }
  }

  #isSafeUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin)
      return parsed.protocol === "https:" || parsed.protocol === "http:"
    } catch {
      return false
    }
  }

  #scheduleAutoDismiss() {
    this.#autoDismissTimer = setTimeout(() => this.close(), this.dismissDelayValue)
  }

  // Failsafe for missed real-time broadcasts (i.e. ActionCable disconnects on
  // backgrounded tabs, subscribe-after-publish races on newly-opened tabs).
  // The interval is intentionally long: a real broadcast replaces the element,
  // which disconnects this controller and starts a fresh interval on the new
  // node, so this only fires when nothing else has updated us in a while.
  #scheduleRefresh() {
    if (!this.hasIdValue) return

    this.#refreshTimer = setTimeout(() => this.#refresh(), this.refreshIntervalValue)
  }

  async #refresh() {
    try {
      const response = await fetch(`/background_progresses/${this.idValue}`, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        credentials: "same-origin",
      })

      if (response.ok) {
        const html = await response.text()
        if (html) Turbo.renderStreamMessage(html)
      }
    } catch {
      // Network blip or similar transient failure; just try again next tick.
    }

    // If the response replaced our element with a new one, this controller
    // has already been disconnected and the new node owns its own timer.
    // Otherwise (no change, error, or removed) reschedule from here so the
    // watchdog keeps ticking.
    if (this.element.isConnected && REFRESHABLE_STATUSES.has(this.statusValue)) {
      this.#scheduleRefresh()
    }
  }
}
